import { useTranslation, Trans } from "react-i18next";
import type { ManageOrderData, Booking } from "../../../common/jsonRpcTypes";
import PaymentProcessorSwitch from "../shared/PaymentProcessorSwitch";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { useVenueCurrency } from "../hooks/useVenueCurrency";
import { canUseStripeConnect } from "../../utils/paymentProcessorUtils";
import { isCreditCardPaymentDisabledForBooking } from "../../utils/paymentHelpers";
import AlertBanner from "../shared/AlertBanner";
import CreditCardDisabledNotice from "../shared/CreditCardDisabledNotice";

interface PaymentStageProps {
  bookingId: string;
  manageOrderData: ManageOrderData;
  bookingData: Booking;
  onPaymentSuccess: (paymentDetails?: any) => void;
  onBack: () => void;
  submitting: boolean;
}

const PaymentStage: React.FC<PaymentStageProps> = ({
  bookingId,
  manageOrderData,
  bookingData,
  onPaymentSuccess,
  onBack,
  submitting,
}) => {
  const { t } = useTranslation();
  const { currency, countryCode } = useVenueCurrency(bookingData.venue);

  const isTaxExclusive =
    manageOrderData.tax_inclusive_orders === false &&
    Array.isArray(manageOrderData.aggregated_tax_breakdown) &&
    manageOrderData.aggregated_tax_breakdown.length > 0;
  const taxLabel = manageOrderData.tax_config?.tax_label ?? "VAT";

  const canShowStripeConnect = canUseStripeConnect(bookingData);

  // Collins refuses credit-funded cards for this booking type, so warn the guest
  // before they enter card details and let the payment form refuse the card
  // before it is charged.
  const creditCardsDisabled = isCreditCardPaymentDisabledForBooking(bookingData);

  // Extract only essential payment fields to avoid 413 payload too large error
  // and wrap in payment_model structure expected by the backend
  const extractPaymentDetails = (paymentResponse: unknown) => {
    if (!paymentResponse || typeof paymentResponse !== "object") {
      return undefined;
    }

    const payment = paymentResponse as Record<string, unknown>;

    // Extract only the essential fields needed by the backend
    const paymentModel: Record<string, unknown> = {};

    if (payment.payment_ref) paymentModel.payment_ref = payment.payment_ref;
    if (payment.amount !== undefined) paymentModel.amount = payment.amount;
    if (payment.currency) paymentModel.currency = payment.currency;
    if (payment.processor) paymentModel.processor = payment.processor;
    if (payment.card_type) paymentModel.card_type = payment.card_type;
    if (payment.card_last_four) paymentModel.card_last_four = payment.card_last_four;
    if (payment.card_funding) paymentModel.card_funding = payment.card_funding;
    if (payment.created_date) paymentModel.created_date = payment.created_date;
    if (payment.venue_id) paymentModel.venue_id = payment.venue_id;
    if (payment.venue_group_id) paymentModel.venue_group_id = payment.venue_group_id;

    return { payment_model: paymentModel };
  };

  const freedomPayBookingData = {
    ...bookingData,
    date: bookingData.date || "",
    depositTotal: parseFloat(bookingData.depositTotal?.replace(/[£$,]/g, "") ?? "0"),
    venue: {
      title: bookingData.venue.title,
      currency: bookingData.venue.currency,
      country_code: bookingData.venue.country_code,
      collins_features: bookingData.venue.collins_features,
    },
  };

  return (
    <div className="payment-stage">
      <div className="gcw:grid gcw:grid-cols-1 gcw:gap-4">
        <div>
          <div className="gcw:border gcw:border-gray-200 gcw:bg-white">
            <div className="gcw:p-bs-3">
              <h5 className="card-title gcw:font-bold">{t("payment_details", "Payment Details")}</h5>

              <AlertBanner variant="info" className="widget-panel-notice gcw:my-4">
                <Trans
                  i18nKey="payment_now_required"
                  values={{ amount: formatCurrency(manageOrderData.requiredPaymentAmount, currency, countryCode) }}
                  components={{ strong: <strong /> }}
                />
              </AlertBanner>

              <div className="payment-summary gcw:mb-4">
                <div className="gcw:flex gcw:justify-between gcw:items-center">
                  <div>
                    <strong>
                      {t("amount_to_pay", "Amount to Pay")}
                      {!isTaxExclusive && ` (${t("inc_vat", "inc {{taxLabel}}", { taxLabel })})`}:
                    </strong>
                  </div>
                  <div className="gcw:text-right">
                    <strong className="gcw:text-error">
                      {formatCurrency(manageOrderData.requiredPaymentAmount, currency, countryCode)}
                    </strong>
                  </div>
                </div>
              </div>
              {creditCardsDisabled && <CreditCardDisabledNotice />}
              <div id="gc-myo-payment-processor">
                <PaymentProcessorSwitch
                  useStripeConnect={canShowStripeConnect}
                  stripeProps={{
                    bookingId,
                    action: "manage-your-order",
                    disableCreditCards: creditCardsDisabled,
                  }}
                  freedomPayProps={{
                    bookingId,
                    bookingData: freedomPayBookingData,
                    action: "manage-your-order",
                    onPaymentSuccess: (_email, _amount, paymentDetails) =>
                      onPaymentSuccess(extractPaymentDetails(paymentDetails)),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStage;
